import { Component, For, Show, createSignal, createMemo } from "solid-js";
import { useAuth } from "../store/authStore";
import { trace } from "../lib/trace";

/** Detect whether a string looks like a UUID (e.g. "d3f4a1b2-...") */
const isUuidLike = (s: string): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);

/** Return the best human-readable label for a client */
const getClientDisplayName = (client: { name: string; slug?: string; domain?: string | null; id: string }): string => {
  // If name is NOT a UUID, use it as-is
  if (client.name && !isUuidLike(client.name)) return client.name;
  // Fallback to slug, then domain, then abbreviated ID
  if (client.slug && !isUuidLike(client.slug)) return client.slug;
  if (client.domain) return client.domain;
  return client.id.slice(0, 8) + "...";
};

/** Map role names to CSS class suffix for the role pill */
const getRoleClass = (roleName: string): string => {
  const lower = roleName.toLowerCase();
  if (lower.includes("admin") || lower.includes("administrator")) return "csw-role-pill--admin";
  if (lower.includes("owner")) return "csw-role-pill--owner";
  if (lower.includes("editor") || lower.includes("write")) return "csw-role-pill--editor";
  if (lower.includes("viewer") || lower.includes("reader") || lower.includes("read")) return "csw-role-pill--viewer";
  return "csw-role-pill--default";
};

/** SVG chevron-up-down selector icon */
const ChevronUpDown: Component = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    class="csw-icon"
    aria-hidden="true">
    <path
      d="M5 6.5L8 3.5L11 6.5"
      stroke="var(--text-secondary)"
      stroke-width="1.5"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      d="M5 9.5L8 12.5L11 9.5"
      stroke="var(--text-secondary)"
      stroke-width="1.5"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
);

/** SVG check icon for selected indicator */
const CheckIcon: Component = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    class="csw-icon"
    aria-hidden="true">
    <path
      d="M3.5 8.5L6.5 11.5L12.5 4.5"
      stroke="var(--brand-primary)"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
);

/** Small loading spinner */
const Spinner: Component = () => (
  <svg aria-hidden="true"
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    class="csw-spinner"
    aria-label="Switching account">
    <circle
      cx="9"
      cy="9"
      r="7"
      stroke="var(--text-secondary)"
      stroke-width="2"
      stroke-opacity="0.25"
    />
    <path
      d="M9 2C5.13 2 2 5.13 2 9"
      stroke="var(--brand-primary)"
      stroke-width="2"
      stroke-linecap="round"
    />
  </svg>
);

export const ClientSwitcher: Component = () => {
  const { currentClient, availableClients, switchClient, isSuperAdmin } = useAuth();

  const currentRole = createMemo(() => {
    const client = currentClient();
    if (!client) return null;
    const assignment = availableClients().find((a) => a.client.id === client.id);
    return assignment?.role.name || null;
  });
  const [isOpen, setIsOpen] = createSignal(false);
  const [switching, setSwitching] = createSignal(false);
  const [searchQuery, setSearchQuery] = createSignal("");

  const handleSwitch = async (clientId: string) => {
    if (switching() || currentClient()?.id === clientId) return;
    trace.state("Client switch initiated", { clientId });
    setSwitching(true);
    try {
      await switchClient(clientId);
      trace.state("Client switch completed", { clientId });
    } finally {
      setSwitching(false);
      setIsOpen(false);
      setSearchQuery("");
    }
  };

  const handleToggle = () => {
    if (switching()) return;
    const next = !isOpen();
    setIsOpen(next);
    if (!next) {
      setSearchQuery("");
    }
  };

  const clients = () => availableClients();

  const filteredClients = createMemo(() => {
    const query = searchQuery().toLowerCase().trim();
    const all = clients();
    if (!query) return all;
    return all.filter(
      (a) =>
        a.client.name.toLowerCase().includes(query) ||
        (a.client.slug && a.client.slug.toLowerCase().includes(query)) ||
        (a.client.domain && a.client.domain.toLowerCase().includes(query)),
    );
  });

  const showSearch = createMemo(() => clients().length > 4);

  // Don't show if user only has one client and not super admin
  const shouldShow = () => clients().length > 1 || isSuperAdmin();

  return (
    <Show when={shouldShow()}>
      <div class="csw-wrapper">
        {/* Trigger button */}
        <button
          class={`csw-trigger${switching() ? " csw-trigger--switching" : ""}`}
          onClick={handleToggle}
          disabled={switching()}
          aria-expanded={isOpen()}
          aria-haspopup="true"
          aria-label="Switch client account">
          <div class="csw-trigger__body">
            <div class="csw-trigger__name-row">
              <span>{currentClient() ? getClientDisplayName(currentClient()!) : "Select Client"}</span>
              <Show when={currentRole()}>
                <span class={`csw-role-pill ${getRoleClass(currentRole()!)}`}>{currentRole()}</span>
              </Show>
              <Show when={clients().length > 1}>
                <span class="csw-trigger__count">
                  {clients().length}
                </span>
              </Show>
            </div>
            <Show when={currentClient()?.domain}>
              <div class="csw-trigger__domain">
                {currentClient()!.domain}
              </div>
            </Show>
          </div>

          <Show when={switching()} fallback={<ChevronUpDown />}>
            <Spinner />
          </Show>
        </button>

        {/* Dropdown panel - always rendered for CSS transition, visibility controlled via styles */}
        <div
          class={`csw-dropdown${isOpen() ? " csw-dropdown--open" : ""}`}>
          <div class="csw-dropdown__inner">
            {/* Super admin header */}
            <Show when={isSuperAdmin()}>
              <div class="csw-dropdown__header">
                All Clients (Super Admin)
              </div>
            </Show>

            {/* Search input - shown when more than 4 clients */}
            <Show when={showSearch()}>
              <div class="csw-search">
                <input
                  class="csw-search__input"
                  type="text"
                  placeholder="Search accounts..."
                  aria-label="Search accounts"
                  value={searchQuery()}
                  onInput={(e) => setSearchQuery(e.currentTarget.value)}
                />
              </div>
            </Show>

            {/* Client list */}
            <For each={filteredClients()}>
              {(assignment) => {
                const isSelected = () => currentClient()?.id === assignment.client.id;
                const roleClass = getRoleClass(assignment.role.name);

                return (
                  <button
                    class={`csw-item${isSelected() ? " csw-item--selected" : ""}${switching() ? " csw-item--switching" : ""}`}
                    onClick={() => handleSwitch(assignment.client.id)}
                    disabled={switching()}>
                    <div class="csw-item__row">
                      {/* Avatar / logo */}
                      <Show
                        when={assignment.client.logo_url}
                        fallback={
                          <div class="csw-item__avatar">
                            {getClientDisplayName(assignment.client)[0]}
                          </div>
                        }>
                        <img
                          class="csw-item__logo"
                          src={assignment.client.logo_url!}
                          alt={assignment.client.name}
                        />
                      </Show>

                      {/* Client info */}
                      <div class="csw-item__info">
                        <div class="csw-item__name">
                          {getClientDisplayName(assignment.client)}
                        </div>
                        <Show when={assignment.client.domain}>
                          <div class="csw-item__domain">
                            {assignment.client.domain}
                          </div>
                        </Show>
                        <div class="csw-item__meta">
                          {/* Role pill */}
                          <span class={`csw-role-pill ${roleClass}`}>
                            {assignment.role.name}
                          </span>
                          <Show when={assignment.is_primary}>
                            <span class="csw-item__primary">
                              Primary
                            </span>
                          </Show>
                        </div>
                      </div>

                      {/* Selected check icon */}
                      <Show when={isSelected()}>
                        <CheckIcon />
                      </Show>
                    </div>
                  </button>
                );
              }}
            </For>

            {/* Empty states */}
            <Show when={clients().length === 0}>
              <div class="csw-empty">
                No clients available
              </div>
            </Show>

            <Show when={clients().length > 0 && filteredClients().length === 0}>
              <div class="csw-empty csw-empty--filtered">
                No accounts match "{searchQuery()}"
              </div>
            </Show>
          </div>
        </div>

        {/* Click outside overlay to close */}
        <Show when={isOpen()}>
          <div
            class="csw-overlay"
            onClick={() => {
              setIsOpen(false);
              setSearchQuery("");
            }}
          />
        </Show>
      </div>
    </Show>
  );
};
